/**
 * JourneyBreadcrumb — the global navigation-journey bar.
 *
 * Rendered ONCE (in Layout, above the routed <Outlet/>). It reads the actual
 * navigation trail from history state via useJourneyView() and shows:
 *   Ancestor > Ancestor > … > Current
 * Ancestors are clickable (navigate directly to their saved URL, restoring that
 * page's list-state/scroll); the current node is active and non-clickable.
 *
 * It renders nothing when there is no trail (root pages, direct URL entry), so it
 * never fabricates a hierarchy. Long trails collapse the middle behind an
 * overflow menu so earlier nodes stay reachable and the layout never breaks.
 *
 * Built on the existing design-system breadcrumb primitives (ui/breadcrumb).
 */
import React from 'react';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
} from '@/components/ui/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { useJourneyView } from '@/hooks/useJourney';
import { compactTrail } from '@/lib/journey';

function AncestorLink({ entry, index, onNavigate }) {
  return (
    <BreadcrumbLink
      href={entry.url}
      title={entry.label || entry.url}
      className="max-w-[10rem] truncate cursor-pointer"
      onClick={(e) => {
        // Left-click with no modifier → SPA navigation; let modified clicks and
        // middle-clicks fall through to the browser (open in new tab, etc.).
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
          return;
        }
        e.preventDefault();
        onNavigate(index);
      }}
    >
      {entry.label || 'Back'}
    </BreadcrumbLink>
  );
}

export default function JourneyBreadcrumb() {
  const { journey, currentLabel, goToAncestor } = useJourneyView();

  // No real trail → render nothing (root page / direct entry / bookmark).
  if (!journey || journey.length === 0) return null;

  const { head, overflow, tail } = compactTrail(journey, { maxVisible: 4, tailCount: 2 });

  return (
    <Breadcrumb className="mb-3">
      <BreadcrumbList>
        {head && (
          <>
            <BreadcrumbItem>
              <AncestorLink entry={head.entry} index={head.index} onNavigate={goToAncestor} />
            </BreadcrumbItem>
            <BreadcrumbSeparator />
          </>
        )}

        {overflow.length > 0 && (
          <>
            <BreadcrumbItem>
              <DropdownMenu>
                <DropdownMenuTrigger
                  className="flex items-center gap-1 rounded-sm outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Show ${overflow.length} more`}
                >
                  <BreadcrumbEllipsis />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {overflow.map(({ entry, index }) => (
                    <DropdownMenuItem
                      key={`${entry.key}-${index}`}
                      onClick={() => goToAncestor(index)}
                      className="max-w-[16rem] truncate"
                      title={entry.label || entry.url}
                    >
                      {entry.label || entry.url}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
          </>
        )}

        {tail.map(({ entry, index }) => (
          <React.Fragment key={`${entry.key}-${index}`}>
            <BreadcrumbItem>
              <AncestorLink entry={entry} index={index} onNavigate={goToAncestor} />
            </BreadcrumbItem>
            <BreadcrumbSeparator />
          </React.Fragment>
        ))}

        <BreadcrumbItem>
          <BreadcrumbPage className="max-w-[14rem] truncate font-medium">
            {currentLabel || '…'}
          </BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}
